import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../../../store/store';
import type { Order, Product } from '../../../../../../types/common';
import styles from './OrderDetailsPanel.module.scss';

type Props = {
    order: Order;
    onBack: () => void;
    onRepeatOrder: (order: Order) => void;
    onPayOrder: (order: Order) => void;
};

const BASE_URL = String(process.env.REACT_APP_SERVER_URL || '').trim();
// const HARD_CODED_DELIVERY_DATE = '20.09.2026';
const PICKUP_ADDRESS = 'МО, г.о. Химки, с. Чашниково, ул. Промышленная, стр. 1 корп. 4, Пн–Пт: 10:00–18:00';

const safeNum = (value: unknown, fallback = 0): number => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const getOrderItemQuantity = (value: unknown): number => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return 0;
    return Math.floor(num);
};

const toMoney = (value: unknown): string => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount.toLocaleString('ru-RU') : '0';
};

const readString = (obj: unknown, key: string): string => {
    if (!obj || typeof obj !== 'object') return '';
    const value = (obj as Record<string, unknown>)[key];
    return typeof value === 'string' ? value.trim() : '';
};

const readProductColor = (obj: unknown): string => {
    return readString(obj, 'productСolor') || readString(obj, 'productColor');
};

const isPendingOrder = (order: Order): boolean => {
    return (
        String(order?.orderStatus ?? '')
            .trim()
            .toLowerCase() === 'pending'
    );
};

const isCompletedOrder = (order: Order): boolean => {
    return (
        String(order?.orderStatus ?? '')
            .trim()
            .toLowerCase() === 'completed'
    );
};

const isPickupOrder = (order: Order): boolean => {
    return !order?.delivery || !order?.delivery?.method;
};

const getDeliveryCostLabel = (order: Order): string => {
    if (isPickupOrder(order)) return 'Самовывоз';
    const cost = safeNum(order?.delivery?.cost, 0);
    if (cost <= 0) return 'Бесплатно';
    return `${toMoney(cost)} ₽`;
};

const getDeliveryMethodLabel = (order: Order): string => {
    return isPickupOrder(order) ? 'Самовывоз' : 'Курьером';
};

const getPaymentMethodLabel = (order: Order): string => {
    const raw = String(order?.paymentMethod ?? '')
        .trim()
        .toLowerCase();
    if (raw === 'sber_bnpl') return 'Плати частями';
    if (raw === 'bank_card') return 'Картой';
    if (raw === 'sbp') return 'СБП';
    return '-';
};

const getDeliveryStatusLabel = (order: Order): string => {
    const raw = String(order?.orderStatus ?? '')
        .trim()
        .toLowerCase();
    const pickup = isPickupOrder(order);
    if (raw === 'paid') return 'Собирается';
    if (raw === 'assembled') return pickup ? 'Готов к выдаче' : 'Собран';
    if (raw === 'indelivery' || raw === 'in_delivery') return 'Доставляется';
    if (raw === 'completed') return pickup ? 'Получен' : 'Доставлен';
    if (raw === 'pending') return 'Ждет оплаты';
    if (raw === 'cancelled' || raw === 'canceled') return 'Отменен';
    return 'Статус уточняется';
};

const readImageId = (value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (!value || typeof value !== 'object') return '';
    const obj = value as Record<string, unknown>;
    if (typeof obj.$oid === 'string') return obj.$oid.trim();
    if (typeof obj.id === 'string') return obj.id.trim();
    if (typeof obj._id === 'string') return obj._id.trim();
    if (obj._id && typeof obj._id === 'object') return readImageId(obj._id);
    return '';
};

const pushImageId = (target: string[], value: unknown) => {
    const id = readImageId(value);
    if (!id || target.includes(id)) return;
    target.push(id);
};

const collectPrimaryImageIds = (source: unknown): string[] => {
    if (!source || typeof source !== 'object') return [];
    const obj = source as Record<string, unknown>;
    const ids: string[] = [];
    pushImageId(ids, obj.image);
    pushImageId(ids, obj.imageId);
    if (obj.images && typeof obj.images === 'object') {
        const images = obj.images as Record<string, unknown>;
        pushImageId(ids, images.image);
    }
    return ids;
};

const buildProductImageUrl = (imageId: string): string => {
    if (!BASE_URL || !imageId) return '';
    return `${BASE_URL}/products/images/${encodeURIComponent(imageId)}`;
};

const normalizeGroupLine = (group: unknown): string => {
    if (!group || typeof group !== 'object') return '';
    const url = readString(group, 'url');
    const optionValue = readString(group, 'optionValue');
    if (!optionValue) return '';
    const urlLower = url.toLowerCase();
    if (urlLower.includes('размер')) return `Размер ${optionValue}`;
    if (urlLower.includes('цвет') || urlLower.includes('дизайн')) return `${url}: ${optionValue}`;
    return optionValue;
};

const resolveProductHref = (item: any, product: Product | null): string => {
    const slug = readString(item, 'slug') || readString(product, 'slug');
    if (slug) return `/${slug}`;
    const categoryUrl = readString(item, 'categoryUrl') || readString(product?.category, 'url');
    const productId = readString(item, 'productId') || readString(item, 'id') || readString(product, 'id');
    if (!categoryUrl || !productId) return '';
    return `/catalog/${encodeURIComponent(categoryUrl)}/${encodeURIComponent(productId)}`;
};

const resolveItemSecondaryLine = (item: any, product: Product | null): string => {
    return (
        readString(item, 'nameDescription') ||
        readString(product, 'nameDescription') ||
        readString(item?.fillerType, 'name') ||
        readString(item?.fabricType, 'name') ||
        readString(product?.fillerType, 'name') ||
        readString(product?.fabricType, 'name')
    );
};

const resolveItemColorLine = (item: any, product: Product | null): string => {
    const rawColor = readProductColor(item) || readProductColor(product);
    if (rawColor) return `Дизайн/ Цвет: ${rawColor}`;
    const itemGroupLine = normalizeGroupLine(item?.group);
    if (itemGroupLine && !itemGroupLine.toLowerCase().includes('размер')) return itemGroupLine;
    const productGroupLine = normalizeGroupLine(product?.group);
    if (productGroupLine && !productGroupLine.toLowerCase().includes('размер')) return productGroupLine;
    return '';
};

const resolveItemSizeLine = (item: any, product: Product | null): string => {
    const itemGroupLine = normalizeGroupLine(item?.group);
    if (itemGroupLine.toLowerCase().includes('размер')) return itemGroupLine;
    const productGroupLine = normalizeGroupLine(product?.group);
    if (productGroupLine.toLowerCase().includes('размер')) return productGroupLine;
    return '';
};

const OrderDetailsPanel: FC<Props> = ({ order, onBack, onRepeatOrder, onPayOrder }) => {
    const products = useSelector((state: RootState) => state.product.products);
    const details = order as any;
    const [brokenImageUrls, setBrokenImageUrls] = useState<Record<string, true>>({});

    const resolveOrderItemImages = useCallback(
        (item: any): string[] => {
            const productId = String(item?.productId ?? '').trim();
            const product = productId ? (products.find((candidate) => String(candidate?.id ?? '').trim() === productId) ?? null) : null;
            const imageIds = [...collectPrimaryImageIds(item), ...collectPrimaryImageIds(product)];
            return Array.from(new Set(imageIds)).map(buildProductImageUrl).filter(Boolean);
        },
        [products],
    );

    const handleImageError = useCallback((src: string) => {
        if (!src) return;
        setBrokenImageUrls((prev) => {
            if (prev[src]) return prev;
            return { ...prev, [src]: true };
        });
    }, []);

    const items = useMemo(() => {
        const list = details?.items || details?.products || details?.orderItems || details?.cart || [];
        return Array.isArray(list) ? list : [];
    }, [details]);

    const pending = isPendingOrder(order);
    const completed = isCompletedOrder(order);
    const pickup = isPickupOrder(order);
    // const itemsCount = items.length;

    const itemRows = useMemo(() => {
        return items.map((item: any, index: number) => {
            const productId = String(item?.productId ?? '').trim();
            const product = productId ? (products.find((candidate) => String(candidate?.id ?? '').trim() === productId) ?? null) : null;
            const imageCandidates = resolveOrderItemImages(item);
            const imageSrc = imageCandidates.find((candidate) => !brokenImageUrls[candidate]) || '';

            const quantity = getOrderItemQuantity(item?.quantity);
            const baseUnitPrice = safeNum(item?.price, 0);
            const oldLinePrice = quantity > 0 ? +(baseUnitPrice * quantity).toFixed(2) : 0;
            const currentLinePrice = +safeNum(item?.totalPrice, oldLinePrice).toFixed(2);
            const showOldPrice = quantity > 0 && oldLinePrice > currentLinePrice + 0.01;

            const href = resolveProductHref(item, product);
            const reviewHref = href ? `${href}#reviews` : '';

            return {
                key: `${String(item?.productId ?? item?._id ?? item?.name ?? 'item')}-${index}`,
                name: readString(item, 'name') || readString(product, 'name') || 'Товар',
                secondaryLine: resolveItemSecondaryLine(item, product),
                colorLine: resolveItemColorLine(item, product),
                sizeLine: resolveItemSizeLine(item, product),
                imageSrc,
                href,
                reviewHref,
                quantity,
                currentLinePrice,
                oldLinePrice,
                showOldPrice,
            };
        });
    }, [items, products, resolveOrderItemImages, brokenImageUrls]);

    const itemsBaseTotal = useMemo(() => {
        return +items
            .reduce((sum: number, item: any) => {
                const quantity = getOrderItemQuantity(item?.quantity);
                if (quantity <= 0) return sum;
                const baseUnitPrice = safeNum(item?.price, 0);
                return sum + baseUnitPrice * quantity;
            }, 0)
            .toFixed(2);
    }, [items]);

    const itemsSubtotalAfterItemDiscounts = useMemo(() => {
        return +items
            .reduce((sum: number, item: any) => {
                const quantity = getOrderItemQuantity(item?.quantity);
                if (quantity <= 0) return sum;
                const baseUnitPrice = safeNum(item?.price, 0);
                const baseLineTotal = +(baseUnitPrice * quantity).toFixed(2);
                return sum + safeNum(item?.totalPrice, baseLineTotal);
            }, 0)
            .toFixed(2);
    }, [items]);

    const totalDiscountReported = Math.max(0, safeNum(details?.totalDiscount, 0));
    const fallbackGeneralDiscount = Math.max(0, +(itemsBaseTotal - itemsSubtotalAfterItemDiscounts).toFixed(2));
    const promoCode = details?.promoCode ?? null;

    const promoDiscountMoney = useMemo(() => {
        if (!promoCode) return 0;
        if (promoCode.discountType === 'points') return Math.max(0, safeNum(promoCode.discountValue, 0));
        if (totalDiscountReported > fallbackGeneralDiscount + 0.01) {
            return +(totalDiscountReported - fallbackGeneralDiscount).toFixed(2);
        }
        return 0;
    }, [promoCode, totalDiscountReported, fallbackGeneralDiscount]);

    const generalDiscountMoney = useMemo(() => {
        if (totalDiscountReported > 0) {
            return Math.max(0, +(totalDiscountReported - promoDiscountMoney).toFixed(2));
        }
        return fallbackGeneralDiscount;
    }, [totalDiscountReported, promoDiscountMoney, fallbackGeneralDiscount]);

    const showGeneralDiscount = generalDiscountMoney > 0.01;
    const showPromoDiscount = Boolean(promoCode);

    const promoDiscountLabel = useMemo(() => {
        if (!promoCode) return '';
        if (promoCode.discountType === 'points') return `-${toMoney(promoDiscountMoney)} ₽`;
        if (promoDiscountMoney > 0.01) return `-${toMoney(promoDiscountMoney)} ₽`;
        return `-${safeNum(promoCode.discountValue, 0)}%`;
    }, [promoCode, promoDiscountMoney]);

    const orderTotal = useMemo(() => {
        return safeNum(details?.totalPrice, itemsSubtotalAfterItemDiscounts);
    }, [details?.totalPrice, itemsSubtotalAfterItemDiscounts]);

    const addressValue = useMemo(() => {
        if (pickup) return PICKUP_ADDRESS;
        return details?.user?.address?.raw || details?.address?.raw || '-';
    }, [details, pickup]);

    const handleOpenNewTab = useCallback((href: string) => {
        if (!href || typeof window === 'undefined') return;
        window.open(href, '_blank', 'noopener,noreferrer');
    }, []);

    return (
        <div className={styles.wrapper}>
            <div className={styles.head}>
                <button type="button" className={styles.backButton} onClick={onBack}>
                    <img className={styles.backIcon} src="/icons/new/arrow_left.svg" alt="" aria-hidden="true" />
                    <span>Заказы</span>
                </button>
                <div className={styles.heading}>
                    <h2 className={styles.orderNumber}>{String(details?.orderNumber ?? '-')}</h2>
                    {/* <span className={styles.orderCount}>{itemsCount} шт</span> */}
                </div>
            </div>
            <div className={styles.itemsList}>
                {itemRows.map((item, i) => {
                    const isLast = i === itemRows.length - 1;
                    return (
                        <div key={`item-${item.key}-${i}`}>
                            <article key={`item-${item.key}-${i}`} className={styles.itemCard}>
                                <div className={styles.itemMedia}>
                                    {item.imageSrc ? (
                                        <img
                                            className={styles.itemImage}
                                            src={item.imageSrc}
                                            alt={item.name}
                                            loading="lazy"
                                            onError={() => handleImageError(item.imageSrc)}
                                        />
                                    ) : (
                                        <div className={styles.itemImageStub} aria-hidden="true" />
                                    )}
                                </div>

                                <div className={styles.itemBody}>
                                    <div className={styles.itemInfo}>
                                        {item.href ? (
                                            <a className={styles.itemTitle} href={item.href} target="_blank" rel="noopener noreferrer">
                                                {item.name}
                                            </a>
                                        ) : (
                                            <div className={styles.itemTitle}>{item.name}</div>
                                        )}
                                        {item.secondaryLine ? <div className={styles.itemText}>{item.secondaryLine}</div> : null}
                                        {item.colorLine ? <div className={styles.itemText}>{item.colorLine}</div> : null}
                                        {item.sizeLine ? <div className={styles.itemText}>{item.sizeLine}</div> : null}
                                        <div className={styles.itemText}>Количество: {item.quantity} шт</div>

                                        {completed ? (
                                            <button
                                                type="button"
                                                className={`${styles.reviewButton} ${styles.reviewButtonHidden}`}
                                                onClick={() => handleOpenNewTab(item.reviewHref)}
                                                disabled={!item.reviewHref}
                                            >
                                                Написать отзыв
                                            </button>
                                        ) : null}
                                    </div>

                                    <div className={styles.itemPriceBox}>
                                        <div className={styles.itemPriceCurrent}>{toMoney(item.currentLinePrice)} ₽</div>
                                        {item.showOldPrice ? <div className={styles.itemPriceOld}>{toMoney(item.oldLinePrice)} ₽</div> : null}
                                    </div>
                                </div>
                            </article>
                            {completed ? (
                                <button
                                    type="button"
                                    className={`${styles.reviewButton} ${styles.reviewButtonMobileVisible}`}
                                    onClick={() => handleOpenNewTab(item.reviewHref)}
                                    disabled={!item.reviewHref}
                                >
                                    Написать отзыв
                                </button>
                            ) : (
                                !isLast && <div className={`${styles.greyBorder} ${styles.reviewButtonMobileVisible}`} />
                            )}
                        </div>
                    );
                })}
            </div>

            {!pending ? (
                <button type="button" className={styles.actionButton} onClick={() => void onRepeatOrder(order)}>
                    Повторить заказ
                </button>
            ) : (
                <div className={styles.greyBorder} />
            )}

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Сумма заказа</h3>
                <div className={styles.infoRows}>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Стоимость продуктов</span>
                        <span className={styles.infoLine} aria-hidden="true" />
                        <span className={styles.infoValue}>{toMoney(itemsBaseTotal)} ₽</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Стоимость доставки</span>
                        <span className={styles.infoLine} aria-hidden="true" />
                        <span className={styles.infoValue}>{getDeliveryCostLabel(order)}</span>
                    </div>
                    {showGeneralDiscount ? (
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabelDiscount}>Скидка</span>
                            <span className={styles.infoLine} aria-hidden="true" />
                            <span className={styles.infoValueDiscount}>-{toMoney(generalDiscountMoney)} ₽</span>
                        </div>
                    ) : null}
                    {showPromoDiscount ? (
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabelDiscount}>Скидка по промокоду</span>
                            <span className={styles.infoLine} aria-hidden="true" />
                            <span className={styles.infoValueDiscount}>{promoDiscountLabel}</span>
                        </div>
                    ) : null}
                    <div className={`${styles.infoRow} ${styles.infoRowTotal}`}>
                        <span className={styles.infoLabel}>итого</span>
                        <span className={styles.totalValue}>{toMoney(orderTotal)} ₽</span>
                    </div>
                </div>
            </section>

            {pending ? (
                <button type="button" className={styles.actionButton} onClick={() => void onPayOrder(order)}>
                    Оплатить
                </button>
            ) : (
                <div className={styles.greyBorder} />
            )}

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Информация о заказе</h3>
                <div className={styles.infoRows}>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Номер заказа</span>
                        <span className={styles.infoLine} aria-hidden="true" />
                        <span className={styles.infoValue}>{String(details?.orderNumber ?? '-')}</span>
                    </div>
                    {/* <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Дата доставки</span>
                        <span className={styles.infoLine} aria-hidden="true" />
                        <span className={styles.infoValue}>{HARD_CODED_DELIVERY_DATE}</span>
                    </div> */}
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Статус доставки</span>
                        <span className={styles.infoLine} aria-hidden="true" />
                        <span className={styles.infoValue}>{getDeliveryStatusLabel(order)}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Способ доставки</span>
                        <span className={styles.infoLine} aria-hidden="true" />
                        <span className={styles.infoValue}>{getDeliveryMethodLabel(order)}</span>
                    </div>
                    {getPaymentMethodLabel(order) !== '-' && (
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Способ оплаты</span>
                            <span className={styles.infoLine} aria-hidden="true" />
                            <span className={styles.infoValue}>{getPaymentMethodLabel(order)}</span>
                        </div>
                    )}
                    <div className={`${styles.infoRow} ${styles.infoRowAddress}`}>
                        <span className={styles.infoLabel}>{pickup ? 'Адрес самовывоза' : 'Адрес доставки'}</span>
                        <span className={styles.infoLine} aria-hidden="true" />
                        <span className={`${styles.infoValue} ${styles.infoValueAddress}`}>{addressValue}</span>
                    </div>
                </div>
            </section>
            <div className={styles.greyBorder} />

            <button type="button" className={styles.returnLink} onClick={() => handleOpenNewTab('/customers?section=return')}>
                Узнать условия возврата
            </button>
        </div>
    );
};

export default OrderDetailsPanel;
